import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Badge,
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Icon,
} from "@nexus/ui";
import { getVendorInitials } from "../lib/vendorInitials";
import { connectionNeedsResign } from "../lib/terms-compliance";
import { AdjustGuidelinesDrawer } from "./AdjustGuidelinesDrawer";
import { DisconnectDialog } from "./DisconnectDialog";
import { ReSignSheet } from "./ReSignSheet";
import type { TenantVendorConnection } from "../types/marketplace.types";

interface ConnectionCardProps {
  connection: TenantVendorConnection;
  tenantId: string;
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

export function ConnectionCard({ connection, tenantId }: ConnectionCardProps) {
  const { t } = useTranslation();
  const connectedAt = formatDate(connection.connectedAt);
  const initials = getVendorInitials(connection.vendor.name);
  const vendorInactive = !connection.vendor.isActive;
  const needsResign = !vendorInactive && connectionNeedsResign(connection);
  const [adjustOpen, setAdjustOpen] = useState(false);
  const [disconnectOpen, setDisconnectOpen] = useState(false);
  const [resignOpen, setResignOpen] = useState(false);

  return (
    <>
      <div className="flex items-start gap-3.5 rounded-xl border border-border bg-card p-4">
        {connection.vendor.logoUrl ? (
          <img
            src={connection.vendor.logoUrl}
            alt={connection.vendor.name}
            loading="lazy"
            className="h-11 w-11 shrink-0 rounded-lg object-cover"
          />
        ) : (
          <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-muted text-sm font-semibold text-muted-foreground">
            {initials}
          </div>
        )}
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <p className="truncate text-sm font-semibold text-foreground">
              {connection.vendor.name}
            </p>
            {vendorInactive ? (
              <Badge variant="secondary">
                {t("marketplace.connections.vendorInactive")}
              </Badge>
            ) : (
              <>
                <Badge variant="status-connected">
                  {t("marketplace.catalog.connected")}
                </Badge>
                {needsResign && (
                  <Badge variant="outline" className="border-warning text-warning">
                    {t("marketplace.resign.badge")}
                  </Badge>
                )}
              </>
            )}
          </div>
          {vendorInactive && (
            <p className="mt-1 text-xs text-muted-foreground">
              {t("marketplace.connections.vendorInactiveHint")}
            </p>
          )}
          <p className="mt-1 flex flex-wrap items-center gap-x-1.5 gap-y-0.5 text-xs text-muted-foreground">
            {connection.package && (
              <>
                <span>
                  {t("marketplace.connections.package", {
                    name: connection.package.name,
                  })}
                </span>
                <span className="text-muted-foreground/40" aria-hidden>
                  ·
                </span>
              </>
            )}
            <time dateTime={connection.connectedAt} className="tabular-nums">
              {t("marketplace.connections.connectedAt", { date: connectedAt })}
            </time>
          </p>
        </div>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              size="icon-sm"
              aria-label={t("common.more", "More")}
              className="shrink-0"
            >
              <Icon name="more-horizontal" size="sm" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            {needsResign && (
              <>
                <DropdownMenuItem
                  icon="file-text"
                  className="cursor-pointer"
                  onClick={() => setResignOpen(true)}
                >
                  {t("marketplace.resign.action")}
                </DropdownMenuItem>
                <DropdownMenuSeparator />
              </>
            )}
            <DropdownMenuItem
              icon="pencil"
              className="cursor-pointer"
              disabled={vendorInactive}
              onClick={() => (needsResign ? setResignOpen(true) : setAdjustOpen(true))}
            >
              {t("marketplace.connections.adjustGuidelines")}
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              variant="destructive"
              icon="x-circle"
              className="cursor-pointer"
              onClick={() => setDisconnectOpen(true)}
            >
              {t("marketplace.connections.disconnect")}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      <AdjustGuidelinesDrawer
        connection={connection}
        tenantId={tenantId}
        open={adjustOpen}
        onClose={() => setAdjustOpen(false)}
        onResignRequired={() => {
          setAdjustOpen(false);
          setResignOpen(true);
        }}
      />
      <ReSignSheet
        connectionId={connection.id}
        vendorId={connection.vendorId}
        tenantId={tenantId}
        open={resignOpen}
        onClose={() => setResignOpen(false)}
        onSuccess={() => setResignOpen(false)}
      />
      <DisconnectDialog
        connectionId={connection.id}
        vendorName={connection.vendor.name}
        tenantId={tenantId}
        open={disconnectOpen}
        onClose={() => setDisconnectOpen(false)}
      />
    </>
  );
}
